import { useEffect, useState } from "react";
import { X, ChevronRight, CheckCircle2, Flag, Search, Check, Ban, Link2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/AuthContext";
import { StatusChip, CLIENT_STATUS, BOOKING_STATUS, PAYMENT_STATUS, COMM_LABELS, eur, ACCENT } from "./ptShared";
import { sortRows, SortSelect, firstOf, lastOf, waLink } from "./listSort";

export const INTAKE_STATUS = {
  intake_required: { nl: "REQUIRED", color: "#D4A93A" },
  intake_completed: { nl: "REVIEW", color: "#F97316" },
  reviewed: { nl: "✓ REVIEWED", color: "#4ADE80" },
  needs_follow_up: { nl: "FOLLOW-UP", color: "#60A5FA" },
};

const EXPERIENCE_LABELS = { beginner: "Beginner", some_experience: "Enige ervaring", advanced: "Gevorderd" };
const DAY_LABELS = { monday: "Maandag", tuesday: "Dinsdag", wednesday: "Woensdag", thursday: "Donderdag", friday: "Vrijdag", saturday: "Zaterdag", sunday: "Zondag" };
const TIME_LABELS = { morning: "Ochtend", afternoon: "Middag", evening: "Avond" };


const FILTERS = [
  ["", "All clients"], ["new", "New clients"], ["active", "Active clients"],
  ["planned", "Training planned"], ["done", "Training completed"], ["payment_open", "Payment open"],
  ["paid", "Paid"], ["declined", "Declined"],
  ["intake_required", "Intake required"], ["awaiting_review", "Awaiting review"],
  ["reviewed", "Reviewed"], ["inactive", "Inactive clients"],
];

/* PT Clients — zoeken, sorteren, filters, quick actions + volledig klantprofiel (spec 30–35) */
export default function AdminPTClients({ selectedEmail, onClearSelection, onNewPayment, profileTab }) {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("date_new");
  const load = () => api.get("/pt/admin/clients", { params: filter ? { filter } : {} }).then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const quickStatus = async (e, email, status, label) => {
    e.stopPropagation();
    if (status === "declined" && !window.confirm("Deze PT-aanmelding definitief afwijzen?\n\nDe klant blijft bewaard en is terug te vinden via het filter 'Declined'.")) return;
    try {
      await api.patch(`/pt/admin/clients/${encodeURIComponent(email)}`, { status });
      toast.success(label);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Mislukt"); }
  };

  let shown = items || [];
  const q = query.trim().toLowerCase();
  if (q) shown = shown.filter((c) => [c.name, c.first_name, c.last_name, c.email, c.phone].filter(Boolean).join(" ").toLowerCase().includes(q));
  shown = sortRows(shown, sort, { first: (c) => firstOf(c), last: (c) => lastOf(c), date: (c) => c.registered_at || "" });

  return (
    <div data-testid="pt-clients">
      <div className="flex gap-2 mb-4 flex-wrap">
        {FILTERS.map(([v, label]) => (
          <button key={v} onClick={() => setFilter(v)} className={`chip px-3 py-1.5 border transition-colors ${filter === v ? "text-[#0A0A0A]" : "border-[#F5F3F0]/20 text-[#A3A3A3]"}`}
            style={filter === v ? { background: ACCENT, borderColor: ACCENT } : {}} data-testid={`pt-clients-filter-${v || "all"}`}>{label}</button>
        ))}
      </div>
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Zoek op voornaam, achternaam, e-mail of telefoon…"
            className="editorial-input dark pl-6 w-full" data-testid="pt-clients-search" />
        </div>
        <SortSelect value={sort} onChange={setSort} testid="pt-clients-sort" />
      </div>
      {items === null ? <div className="chip text-[#A3A3A3]">Laden…</div>
        : shown.length === 0 ? <div className="border border-[#F5F3F0]/15 p-8 text-center chip text-[#4A4A4A]" data-testid="pt-clients-empty">Geen klanten in dit filter.</div>
        : <div className="line-t line-dark">{shown.map((c) => (
            <button key={c.email} onClick={() => onClearSelection(c.email)} className="w-full text-left line-b line-dark py-3.5 flex flex-wrap items-center gap-3 hover:bg-[#F5F3F0]/5 transition-colors px-2 -mx-2" data-testid={`pt-client-${c.email}`}>
              <span className="font-display text-lg uppercase flex-1 min-w-32 truncate">{c.name}</span>
              <span className="chip text-[#4A4A4A] hidden lg:inline">{(c.registered_at || "").slice(0, 10)}</span>
              <span className="chip px-2 py-0.5 border" style={{ color: INTAKE_STATUS[c.onboarding_status]?.color, borderColor: `${INTAKE_STATUS[c.onboarding_status]?.color}55` }} data-testid={`pt-client-intake-${c.email}`}>
                {INTAKE_STATUS[c.onboarding_status]?.nl || "REQUIRED"}
              </span>
              <StatusChip map={CLIENT_STATUS} value={c.status} testid={`pt-client-status-${c.email}`} />
              <span className="chip text-[#A3A3A3]">{c.sessions_done} sessies{c.next_session ? ` · volgende ${c.next_session.date}` : " · geen vervolg"}</span>
              {c.open_payments > 0 && <span className="chip px-2 py-0.5 border" style={{ color: "#D4A93A", borderColor: "#D4A93A55" }}>{c.open_payments} BETALING OPEN</span>}
              {c.status === "new" && (
                <span className="flex gap-1.5">
                  <span role="button" tabIndex={0} onClick={(e) => quickStatus(e, c.email, "active", "Aanvraag goedgekeurd")} onKeyDown={(e) => { if (e.key === "Enter") quickStatus(e, c.email, "active", "Aanvraag goedgekeurd"); }}
                    className="chip px-3 py-1.5 border inline-flex items-center gap-1.5 cursor-pointer" style={{ borderColor: "#4ADE80", color: "#4ADE80" }} data-testid={`pt-client-approve-${c.email}`}><Check size={12}/> Goedkeuren</span>
                  <span role="button" tabIndex={0} onClick={(e) => quickStatus(e, c.email, "declined", "Aanvraag afgewezen")} onKeyDown={(e) => { if (e.key === "Enter") quickStatus(e, c.email, "declined", "Aanvraag afgewezen"); }}
                    className="chip px-3 py-1.5 border border-[#F87171]/50 text-[#F87171] inline-flex items-center gap-1.5 cursor-pointer" data-testid={`pt-client-decline-${c.email}`}><Ban size={12}/> Afwijzen</span>
                </span>
              )}
              <ChevronRight size={14} className="text-[#4A4A4A]" />
            </button>
          ))}</div>}
      {selectedEmail && <ClientProfile email={selectedEmail} initialTab={profileTab} onClose={() => { onClearSelection(null); load(); }} onNewPayment={onNewPayment} />}
    </div>
  );
}

const SECTIONS = ["Overview", "Intake", "Bookings", "Traject", "Progress", "Payments", "Notes", "Communicatie"];

function ClientProfile({ email, initialTab, onClose, onNewPayment }) {
  const [d, setD] = useState(null);
  const [sec, setSec] = useState(initialTab || "Overview");
  const [note, setNote] = useState("");
  const [deleteScope, setDeleteScope] = useState(null); // "pt" | "account"
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [linkState, setLinkState] = useState(null); // {url} | "busy" | null — boekingslink voor dit lid
  const createBookingLink = async () => {
    setLinkState("busy");
    try {
      const { data } = await api.post("/pt/admin/links", { name: d.name });
      const url = `${window.location.origin}/pt/boek/${data.link.token}`;
      try { await navigator.clipboard.writeText(url); } catch { /* clipboard niet altijd beschikbaar */ }
      setLinkState({ url });
      toast.success("Boekingslink aangemaakt en gekopieerd");
    } catch (err) {
      setLinkState(null);
      toast.error(formatApiError(err?.response?.data?.detail) || "Link aanmaken mislukt");
    }
  };
  const load = () => api.get(`/pt/admin/clients/${encodeURIComponent(email)}`).then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { setD(null); load(); setSec(initialTab || "Overview"); }, [email]); // eslint-disable-line
  if (!d) return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"><span className="chip text-[#A3A3A3]">Laden…</span></div>
  );
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = d.bookings.filter((b) => b.date >= today && ["pending", "approved"].includes(b.status)).sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
  const past = d.bookings.filter((b) => ["completed", "no_show"].includes(b.status) || (b.date < today && b.status === "approved"));
  const cancelled = d.bookings.filter((b) => ["cancelled", "declined"].includes(b.status));
  const profile = d.profile || {};
  const intake = d.intake;

  const setStatus = async (status) => {
    try { await api.patch(`/pt/admin/clients/${encodeURIComponent(email)}`, { status }); toast.success("Status bijgewerkt"); load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Mislukt"); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.patch(`/pt/admin/clients/${encodeURIComponent(email)}`, { note }); toast.success("Notitie toegevoegd"); setNote(""); load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Mislukt"); }
  };
  const review = async (action, createTodo = false, reviewNote = "") => {
    try {
      await api.post(`/pt/admin/clients/${encodeURIComponent(email)}/intake/review`, { action, note: reviewNote, create_todo: createTodo });
      toast.success(action === "reviewed" ? "Intake gemarkeerd als REVIEWED" : "Gemarkeerd als NEEDS FOLLOW-UP");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Mislukt"); }
  };

  const permanentDelete = async () => {
    if (!deleteScope || deleteConfirm.trim().toLowerCase() !== email.toLowerCase()) return;
    setDeleteBusy(true);
    try {
      const path = deleteScope === "pt" ? "delete-pt-data" : "delete-account";
      await api.post(`/pt/admin/clients/${encodeURIComponent(email)}/${path}`, { confirm_email: deleteConfirm });
      toast.success(deleteScope === "pt" ? "PT-data permanent verwijderd" : "Account permanent verwijderd");
      setDeleteScope(null);
      setDeleteConfirm("");
      if (deleteScope === "account") onClose();
      else load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Verwijderen mislukt");
    }
    setDeleteBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pt-client-profile">
        <div className="flex items-start justify-between mb-1">
          <div className="chip text-[#A3A3A3]">// PT-KLANTPROFIEL</div>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white" data-testid="pt-client-close"><X size={16}/></button>
        </div>
        <div className="font-display uppercase text-3xl mb-1">{d.name}</div>
        <div className="chip text-[#4A4A4A] mb-4">
          {d.email}
          {d.phone ? (
            <> · <a href={waLink(d.phone)} target="_blank" rel="noreferrer" className="text-[#A3A3A3] hover:text-[#4ADE80] transition-colors" title="Open in WhatsApp" data-testid="pt-profile-whatsapp">{d.phone}</a></>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2 mb-5">
          <span className="chip px-2 py-0.5 border" style={{ color: INTAKE_STATUS[d.onboarding_status]?.color, borderColor: `${INTAKE_STATUS[d.onboarding_status]?.color}55` }} data-testid="pt-profile-intake-badge">{INTAKE_STATUS[d.onboarding_status]?.nl}</span>
          <StatusChip map={CLIENT_STATUS} value={d.status} />
          {linkState && linkState !== "busy" ? (
            <a href={`https://wa.me/?text=${encodeURIComponent(`Hoi ${d.name.split(" ")[0]}! Via deze persoonlijke link boek je direct je Personal Training bij JOH Boxing: ${linkState.url}`)}`}
              target="_blank" rel="noreferrer"
              className="chip px-2 py-0.5 border border-[#4ADE80]/50 text-[#4ADE80] inline-flex items-center gap-1.5 hover:bg-[#4ADE80]/10 transition-colors"
              title={linkState.url} data-testid="pt-profile-link-share">
              <Link2 size={11} /> Link gekopieerd — deel via WhatsApp
            </a>
          ) : (
            <button onClick={createBookingLink} disabled={linkState === "busy"}
              className="chip px-2 py-0.5 border border-[#F5F3F0]/25 text-[#A3A3A3] hover:text-[#F5F3F0] inline-flex items-center gap-1.5 transition-colors disabled:opacity-40"
              title="Maak een persoonlijke boekingslink voor dit lid" data-testid="pt-profile-create-link">
              <Link2 size={11} /> {linkState === "busy" ? "Maken…" : "Boekingslink"}
            </button>
          )}
          <button type="button" onClick={() => { setDeleteScope("pt"); setDeleteConfirm(""); }}
            className="chip px-2 py-0.5 border border-red-400/40 text-red-300 hover:bg-red-400/10 inline-flex items-center gap-1.5 transition-colors"
            data-testid="pt-client-delete-pt">
            <Trash2 size={11} /> Delete PT data
          </button>
          <button type="button" onClick={() => { setDeleteScope("account"); setDeleteConfirm(""); }}
            className="chip px-2 py-0.5 border border-red-500/60 text-red-300 hover:bg-red-500/10 inline-flex items-center gap-1.5 transition-colors"
            data-testid="pt-client-delete-account">
            <Trash2 size={11} /> Delete entire account
          </button>
        </div>

        {d.status === "new" && (
          <div className="flex flex-wrap gap-2 mb-6 border border-[#D4A93A]/40 bg-[#D4A93A]/5 p-4" data-testid="pt-profile-review-actions">
            <span className="chip text-[#A3A3A3] w-full">— NIEUWE AANVRAAG BEOORDELEN</span>
            <button onClick={() => setStatus("active")} className="chip px-4 py-2.5 inline-flex items-center gap-2" style={{ background: "#4ADE80", color: "#0A0A0A" }} data-testid="pt-profile-approve"><Check size={13}/> GOEDKEUREN</button>
            <button onClick={() => { if (window.confirm("Deze PT-aanmelding definitief afwijzen?\n\nDe klant blijft bewaard en is terug te vinden via het filter 'Declined'.")) setStatus("declined"); }}
              className="chip px-4 py-2.5 border border-[#F87171]/60 text-[#F87171] inline-flex items-center gap-2" data-testid="pt-profile-decline"><Ban size={13}/> AFWIJZEN</button>
          </div>
        )}
        {d.status === "declined" && (
          <div className="mb-6 border border-[#F87171]/40 bg-[#F87171]/5 p-4 flex flex-wrap items-center gap-3" data-testid="pt-profile-declined-note">
            <span className="chip text-[#F87171]">DEZE AANMELDING IS AFGEWEZEN — de klant blijft bewaard.</span>
            <button onClick={() => setStatus("active")} className="chip px-3 py-1.5 border border-[#4ADE80]/60 text-[#4ADE80]" data-testid="pt-profile-reapprove">Toch goedkeuren</button>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-6">
          {SECTIONS.map((s) => (
            <button key={s} onClick={() => setSec(s)} className={`chip px-3 py-1.5 border transition-colors ${sec === s ? "text-[#0A0A0A]" : "border-[#F5F3F0]/20 text-[#A3A3A3]"}`}
              style={sec === s ? { background: ACCENT, borderColor: ACCENT } : {}} data-testid={`pt-client-sec-${s.toLowerCase()}`}>{s}</button>
          ))}
        </div>

        {sec === "Overview" && (
          <div className="space-y-4" data-testid="pt-client-overview">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="chip text-[#A3A3A3]">Status</label>
                <select value={d.status} onChange={(e) => setStatus(e.target.value)} className="editorial-input dark" data-testid="pt-client-status-select">
                  {Object.entries(CLIENT_STATUS).map(([k, v]) => <option key={k} value={k} className="bg-[#0A0A0A]">{v.nl}</option>)}
                </select>
              </div>
              <div><label className="chip text-[#A3A3A3]">Boxing level</label><div className="text-sm mt-2 text-[#DDD6CB]">{intake?.boxing_level || profile.experience || "—"}</div></div>
              <div><label className="chip text-[#A3A3A3]">Main goal</label><div className="text-sm mt-2 text-[#DDD6CB]">{intake?.main_goal || profile.goal || "—"}</div></div>
              <div><label className="chip text-[#A3A3A3]">Geboortedatum / geslacht</label><div className="text-sm mt-2 text-[#DDD6CB]">{d.user?.dob || "—"} · {d.user?.gender === "male" ? "Man" : d.user?.gender === "female" ? "Vrouw" : "—"}</div></div>
              <div><label className="chip text-[#A3A3A3]">Pakketvoorkeur</label><div className="text-sm mt-2 text-[#DDD6CB]" data-testid="pt-client-pref">{profile.package_preference ? profile.package_preference.toUpperCase() : "—"}</div></div>
              <div><label className="chip text-[#A3A3A3]">Registratie-ervaring</label><div className="text-sm mt-2 text-[#DDD6CB]" data-testid="pt-client-reg-experience">{EXPERIENCE_LABELS[profile.boxing_experience] || "—"}</div></div>
              <div><label className="chip text-[#A3A3A3]">Voorkeursdagen</label><div className="text-sm mt-2 text-[#DDD6CB]" data-testid="pt-client-pref-days">{(profile.preferred_training_days || []).map((x) => DAY_LABELS[x] || x).join(", ") || "—"}</div></div>
              <div><label className="chip text-[#A3A3A3]">Voorkeursmomenten</label><div className="text-sm mt-2 text-[#DDD6CB]" data-testid="pt-client-pref-times">{(profile.preferred_training_times || []).map((x) => TIME_LABELS[x] || x).join(", ") || "—"}</div></div>
              <div><label className="chip text-[#A3A3A3]">Noodcontact</label><div className="text-sm mt-2 text-[#DDD6CB]" data-testid="pt-client-emergency">{d.user?.emergency_contact?.name ? `${d.user.emergency_contact.name} · ${d.user.emergency_contact.phone || "—"}` : "—"}</div></div>

              <div><label className="chip text-[#A3A3A3]">Actief pakket</label><div className="text-sm mt-2 text-[#DDD6CB]">{d.active_package ? `${d.active_package.label} — ${d.active_package.remaining}/${d.active_package.total} resterend` : "—"}</div></div>
              <div><label className="chip text-[#A3A3A3]">Volgende training</label><div className="text-sm mt-2" style={{ color: ACCENT }}>{d.next_session ? `${d.next_session.date} · ${d.next_session.time}` : "—"}</div></div>
              <div><label className="chip text-[#A3A3A3]">Laatste training</label><div className="text-sm mt-2 text-[#DDD6CB]">{d.last_session ? `${d.last_session.date} · ${d.last_session.time}` : "—"}</div></div>
              <div><label className="chip text-[#A3A3A3]">Sessies gevolgd</label><div className="text-sm mt-2 text-[#DDD6CB]">{d.sessions_done}{d.sessions_total ? ` / ${d.sessions_total}` : ""}</div></div>
              <div><label className="chip text-[#A3A3A3]">Klant sinds</label><div className="text-sm mt-2 text-[#DDD6CB]">{(d.registered_at || "").slice(0, 10) || "—"}</div></div>
            </div>
            {profile.health_notes && (
              <div className="border p-4" style={{ borderColor: "#D4A93A", background: "#D4A93A08" }} data-testid="pt-client-health-notes">
                <div className="chip" style={{ color: ACCENT }}>MEDISCHE AANDACHTSPUNTEN</div>
                <p className="text-sm text-[#DDD6CB] mt-2 whitespace-pre-wrap">{profile.health_notes}</p>
              </div>
            )}
          </div>
        )}

        {sec === "Intake" && (
          <div data-testid="pt-client-intake">
            {/* Compacte samenvatting */}
            <div className="border border-[#F5F3F0]/15 p-4 mb-5">
              <div className="flex flex-wrap gap-3 items-center">
                <span className="chip px-2 py-0.5 border" style={{ color: INTAKE_STATUS[d.onboarding_status]?.color, borderColor: `${INTAKE_STATUS[d.onboarding_status]?.color}55` }}>{INTAKE_STATUS[d.onboarding_status]?.nl}</span>
                {intake && <span className="chip text-[#A3A3A3]">COMPLETED: {(intake.submitted_at || "").replace("T", " ").slice(0, 16)}</span>}
                {profile.reviewed_at && <span className="chip text-[#4ADE80]">REVIEWED: {profile.reviewed_at.replace("T", " ").slice(0, 16)}</span>}
              </div>
              {intake && <div className="text-sm text-[#DDD6CB] mt-3">{intake.boxing_level} · {intake.main_goal}</div>}
            </div>
            {!intake ? (
              <div className="chip text-[#4A4A4A]">Klant heeft de intake nog niet ingediend (INTAKE REQUIRED).</div>
            ) : (
              <div className="space-y-4">
                {d.onboarding_status === "intake_completed" && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    <button onClick={() => review("reviewed")} className="chip px-4 py-2.5 inline-flex items-center gap-2" style={{ background: "#4ADE80", color: "#0A0A0A" }} data-testid="pt-intake-review"><CheckCircle2 size={13}/> MARK AS REVIEWED</button>
                    <button onClick={() => review("follow_up")} className="chip px-4 py-2.5 border border-[#60A5FA]/60 text-[#60A5FA] inline-flex items-center gap-2" data-testid="pt-intake-followup"><Flag size={13}/> MARK AS NEEDS FOLLOW-UP</button>
                  </div>
                )}
                {d.onboarding_status === "needs_follow_up" && (
                  <button onClick={() => review("reviewed")} className="chip px-4 py-2.5 inline-flex items-center gap-2" style={{ background: "#4ADE80", color: "#0A0A0A" }} data-testid="pt-intake-review-2"><CheckCircle2 size={13}/> MARK AS REVIEWED</button>
                )}
                {[
                  ["BOXING LEVEL", intake.boxing_level],
                  ["EXPERIENCE", intake.boxed_before ? `Ja — ${intake.boxing_duration}` : "Nee, nog niet eerder gebokst"],
                  ["MAIN GOAL", intake.main_goal],
                  ["TRAINING GOALS", (intake.goals || []).join(" · ") + (intake.goals_other ? ` (${intake.goals_other})` : "")],
                  ["CURRENT ACTIVITY", `${intake.exercise_frequency} · fitness: ${intake.fitness_level}${intake.other_sport ? ` · sport: ${intake.other_sports}` : ""}`],
                  ["TRAINING PREFERENCES", `${(intake.focus || []).join(" · ")}${intake.train_frequency ? ` · frequentie: ${intake.train_frequency}` : ""}`],
                  ["PHYSICAL CONSIDERATIONS", intake.physical_issues ? intake.physical_details : "Geen"],
                  ["ADDITIONAL INFORMATION", intake.extra || "—"],
                ].map(([label, value]) => (
                  <div key={label}>
                    <label className="chip text-[#A3A3A3]">{label}</label>
                    <div className={`text-sm mt-1 ${label === "PHYSICAL CONSIDERATIONS" && intake.physical_issues ? "text-[#F87171]" : "text-[#DDD6CB]"}`} data-testid={`pt-intake-${label.split(" ")[0].toLowerCase()}`}>{value}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {sec === "Bookings" && (
          <div data-testid="pt-client-agenda">
            {[["Aankomende trainingen", upcoming], ["Eerdere trainingen", past], ["Geannuleerd", cancelled]].map(([title, list]) => (
              <div key={title} className="mb-5">
                <div className="chip text-[#A3A3A3] mb-2">— {title.toUpperCase()}</div>
                {list.length === 0 ? <div className="chip text-[#4A4A4A] py-1">Geen.</div> : (
                  <div className="line-t line-dark">{list.map((b) => (
                    <div key={b.id} className="line-b line-dark py-2.5 flex items-center gap-3">
                      <span className="chip text-[#DDD6CB] w-32">{b.date} · {b.time}</span>
                      <span className="text-sm flex-1">{b.booking_type === "package" ? "Pakket" : "Losse sessie"}{b.traject_id ? " · traject" : ""}</span>
                      <StatusChip map={BOOKING_STATUS} value={b.status} />
                    </div>
                  ))}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {sec === "Traject" && (
          <div data-testid="pt-client-traject">
            {(d.trajects || []).length === 0 ? <div className="chip text-[#4A4A4A]">Geen actieve trajecten — plan er een via de Trajects-tab.</div> : (
              (d.trajects || []).map((t) => (
                <div key={t.id} className="border border-[#F5F3F0]/15 p-4 mb-3">
                  <div className="chip" style={{ color: ACCENT }}>{t.kind === "manual" ? "MANUAL TRAJECT" : "TRAJECT + PAKKET"}</div>
                  <div className="text-sm text-[#DDD6CB] mt-2">Elke week op {t.time} · {t.weeks} weken vanaf {t.start_date}</div>
                </div>
              ))
            )}
          </div>
        )}

        {sec === "Progress" && (
          <div className="space-y-4" data-testid="pt-client-progress">
            <div><label className="chip text-[#A3A3A3]">Huidig focuspunt</label><div className="text-sm mt-1" style={{ color: ACCENT }}>{profile.current_focus || "—"}</div></div>
            <div>
              <label className="chip text-[#A3A3A3]">Milestones</label>
              {(profile.milestones || []).length === 0 ? <div className="chip text-[#4A4A4A] mt-1">Nog geen milestones.</div> : (
                <div className="line-t line-dark mt-2">{profile.milestones.map((m, i) => (
                  <div key={i} className="line-b line-dark py-2 text-sm text-[#DDD6CB]"><span className="chip text-[#4A4A4A] mr-3">{(m.at || "").slice(0, 10)}</span>{m.text}</div>
                ))}</div>
              )}
            </div>
            <div>
              <label className="chip text-[#A3A3A3]">Aandachtspunten</label>
              {(profile.attention_points || []).length === 0 ? <div className="chip text-[#4A4A4A] mt-1">Geen.</div> : (
                <div className="line-t line-dark mt-2">{profile.attention_points.map((a, i) => (
                  <div key={i} className="line-b line-dark py-2 text-sm text-[#DDD6CB]"><span className="chip text-[#4A4A4A] mr-3">{(a.at || "").slice(0, 10)}</span>{a.text}</div>
                ))}</div>
              )}
            </div>
            <div>
              <label className="chip text-[#A3A3A3]">Feedback per sessie</label>
              {d.bookings.filter((b) => b.feedback || b.client_feedback).length === 0 ? <div className="chip text-[#4A4A4A] mt-1">Nog geen feedback.</div> : (
                d.bookings.filter((b) => b.feedback || b.client_feedback).map((b) => (
                  <div key={b.id} className="border border-[#F5F3F0]/15 p-4 mt-2">
                    <div className="chip text-[#A3A3A3] mb-2">{b.date} · {b.time}</div>
                    {b.feedback && <div className="mb-1 text-sm text-[#DDD6CB]">{b.feedback.visible ? "👁 " : "🔒 "}{b.feedback.went_well || b.feedback.focus || b.feedback.next_step}</div>}
                    {b.client_feedback && <div className="text-sm text-[#60A5FA]">Klant: {"★".repeat(b.client_feedback.rating || 0)} {b.client_feedback.comment}</div>}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {sec === "Payments" && (
          <div data-testid="pt-client-payments">
            <button onClick={() => onNewPayment(d.email)} className="chip px-4 py-2.5 border mb-4 inline-block transition-colors" style={{ borderColor: ACCENT, color: ACCENT }} data-testid="pt-client-new-payment">+ Nieuw betaalverzoek</button>
            {d.payments.length === 0 ? <div className="chip text-[#4A4A4A]">Nog geen betaalverzoeken.</div> : (
              <div className="line-t line-dark">{d.payments.map((p) => (
                <div key={p.id} className="line-b line-dark py-3 flex flex-wrap items-center gap-3">
                  <span className="text-sm flex-1 min-w-32">{p.label}</span>
                  <span className="chip text-[#DDD6CB]">{eur(p.amount)}</span>
                  <StatusChip map={PAYMENT_STATUS} value={p.overdue ? "overdue" : p.status} />
                  <span className="chip text-[#4A4A4A]">{(p.sent_at || p.created_at || "").slice(0, 10)}{p.paid_at ? ` → betaald ${p.paid_at.slice(0, 10)}` : ""}</span>
                </div>
              ))}</div>
            )}
          </div>
        )}

        {sec === "Notes" && (
          <div data-testid="pt-client-notes">
            <div className="chip text-[#A3A3A3] mb-3">— INTERNE NOTITIES (alleen Admin)</div>
            {(profile.admin_notes || []).length === 0 ? <div className="chip text-[#4A4A4A] mb-3">Nog geen notities.</div> : (
              <div className="line-t line-dark mb-4">{profile.admin_notes.map((n, i) => (
                <div key={i} className="line-b line-dark py-2.5 text-sm text-[#DDD6CB]"><span className="chip text-[#4A4A4A] mr-3">{(n.at || "").slice(0, 10)}</span>{n.text}</div>
              ))}</div>
            )}
            <div className="flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} className="editorial-input dark flex-1" placeholder="Bv. focus on footwork, prefers Monday morning…" data-testid="pt-client-note-input" />
              <button onClick={addNote} className="chip px-3 py-2 border" style={{ borderColor: ACCENT, color: ACCENT }} data-testid="pt-client-note-add">Toevoegen</button>
            </div>
          </div>
        )}

        {sec === "Communicatie" && (
          <div data-testid="pt-client-comms">
            {(d.communications || []).length === 0 ? <div className="chip text-[#4A4A4A]">Nog geen communicatie gelogd.</div> : (
              <div className="line-t line-dark">{d.communications.map((c) => (
                <div key={c.id} className="line-b line-dark py-2.5 flex items-center gap-3">
                  <span className="chip text-[#4A4A4A] w-24">{(c.at || "").slice(0, 10)}</span>
                  <span className="text-sm text-[#DDD6CB] flex-1">{COMM_LABELS[c.kind] || c.kind}</span>
                </div>
              ))}</div>
            )}
          </div>
        )}

        {deleteScope && (
          <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={() => setDeleteScope(null)}>
            <div className="bg-[#0A0A0A] border border-red-500/50 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="pt-client-delete-confirm">
              <div className="chip text-red-300 mb-2">// PERMANENT VERWIJDEREN</div>
              <h3 className="font-display uppercase text-2xl" data-testid="pt-client-delete-title">
                {deleteScope === "pt" ? "Delete PT data" : "Delete entire account"}
              </h3>
              <p className="text-sm text-[#DDD6CB] mt-3">
                {deleteScope === "pt"
                  ? "Dit verwijdert permanent het PT-profiel, intake, boekingen, pakketten, PT-betalingen, communicatie en PT-mails van deze persoon. Het algemene account blijft bestaan."
                  : "Dit verwijdert permanent het hele account en alle gekoppelde persoonsdata, inclusief PT, boekingen, betalingen, communicatie, groepslessen en membershipgegevens."}
              </p>
              <p className="chip text-red-300 mt-4">Typ ter bevestiging het volledige e-mailadres:</p>
              <div className="chip text-[#A3A3A3] mt-1">{email}</div>
              <input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} className="editorial-input dark w-full mt-3" placeholder={email} data-testid="pt-client-delete-input" />
              <div className="flex gap-3 mt-5">
                <button type="button" onClick={permanentDelete} disabled={deleteBusy || deleteConfirm.trim().toLowerCase() !== email.toLowerCase()}
                  className="chip px-4 py-2.5 bg-red-500 text-[#0A0A0A] disabled:opacity-40" data-testid="pt-client-delete-submit">
                  {deleteBusy ? "…" : "Permanent verwijderen"}
                </button>
                <button type="button" onClick={() => setDeleteScope(null)} className="chip px-4 py-2.5 border border-[#F5F3F0]/25 text-[#DDD6CB]" data-testid="pt-client-delete-cancel">Annuleren</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
